import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

export default function NewsletterPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [rgpd, setRgpd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const dismissed = localStorage.getItem('newsletter_dismissed');
    if (dismissed) return;
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    localStorage.setItem('newsletter_dismissed', '1');
    setVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!rgpd) { setError('Merci d\'accepter les conditions pour continuer.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, rgpd_consent: true })
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409) setError('Cet email est déjà inscrit 🌸');
        else setError(data.detail || 'Une erreur est survenue.');
      } else {
        setDone(true);
        localStorage.setItem('newsletter_dismissed', '1');
      }
    } catch (e) {
      setError('Une erreur est survenue, réessaie plus tard.');
    }
    setLoading(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Overlay doux */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
        onClick={dismiss}
      />

      {/* Carte */}
      <div className="relative w-full max-w-lg overflow-hidden" style={{
        background: '#FBFDFF',
        borderRadius: '2px',
        boxShadow: '0 30px 80px -10px rgba(216,186,195,0.45), 0 4px 20px rgba(0,0,0,0.1)'
      }}>

        {/* Bouton fermer */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
          style={{ color: '#6B6B6B' }}
          onMouseEnter={e => e.currentTarget.style.color = '#000'}
          onMouseLeave={e => e.currentTarget.style.color = '#6B6B6B'}
        >
          <X size={18} />
        </button>

        <div className="flex flex-col sm:flex-row">

          {/* Panneau gauche — déco */}
          <div className="sm:w-2/5 relative flex flex-col items-center justify-center py-10 px-6 sm:py-0"
            style={{ background: 'linear-gradient(160deg, #D8BAC3 0%, #e8cfd6 100%)' }}>
            {/* Motif décoratif */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `radial-gradient(circle at 20% 80%, white 1px, transparent 1px),
                                radial-gradient(circle at 80% 20%, white 1px, transparent 1px),
                                radial-gradient(circle at 50% 50%, white 0.5px, transparent 0.5px)`,
              backgroundSize: '40px 40px, 60px 60px, 20px 20px'
            }} />
            <div className="relative text-center text-white">
              <p className="text-xs font-sans tracking-[0.2em] uppercase opacity-80 mb-2">Ta première visite</p>
              <p className="font-serif font-light leading-none mb-1" style={{fontSize:'72px'}}>-10%</p>
              <div className="mt-4 w-8 h-px bg-white/60 mx-auto" />
              <p className="mt-4 text-xs opacity-70 font-sans leading-relaxed">Code envoyé<br />par e-mail</p>
            </div>
          </div>

          {/* Panneau droit — formulaire */}
          <div className="sm:w-3/5 p-7 sm:p-8">
            {!done ? (
              <>
                <p className="font-serif text-xl mb-2" style={{ color: '#000' }}>
                  Rejoins la newsletter
                </p>
                <p className="text-xs leading-relaxed mb-6" style={{ color: '#6B6B6B' }}>
                  Reçois ton code de bienvenue par e-mail, plus nos conseils beauté et nos offres en avant-première.
                </p>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ton adresse e-mail"
                    className="w-full px-4 py-2.5 text-sm font-sans outline-none transition-all"
                    style={{
                      border: '1px solid #E8DFE3',
                      borderRadius: '2px',
                      background: '#FBFDFF',
                      color: '#000'
                    }}
                    onFocus={e => e.target.style.borderColor = '#D8BAC3'}
                    onBlur={e => e.target.style.borderColor = '#E8DFE3'}
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 text-sm font-sans font-medium tracking-wide transition-opacity"
                    style={{
                      background: '#D8BAC3',
                      color: '#fff',
                      borderRadius: '2px',
                      opacity: loading ? 0.7 : 1,
                      letterSpacing: '0.05em'
                    }}
                  >
                    {loading ? 'Envoi en cours...' : 'Je profite de mes −10 %'}
                  </button>

                  {error && (
                    <p className="text-xs text-center" style={{ color: '#c0392b' }}>{error}</p>
                  )}

                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={rgpd}
                      onChange={(e) => setRgpd(e.target.checked)}
                      className="mt-0.5 flex-shrink-0"
                      style={{ accentColor: '#D8BAC3' }}
                    />
                    <span className="text-xs leading-relaxed" style={{ color: '#6B6B6B' }}>
                      J'accepte de recevoir les actualités de GlowShape Institut. Désinscription possible à tout moment.
                    </span>
                  </label>
                </form>

                <div className="mt-5 text-center">
                  <button
                    onClick={dismiss}
                    className="text-xs transition-colors"
                    style={{ color: '#aaa' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#6B6B6B'}
                    onMouseLeave={e => e.currentTarget.style.color = '#aaa'}
                  >
                    Non merci, je réserve au tarif normal
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                  style={{ background: 'linear-gradient(135deg, #D8BAC3, #e8cfd6)' }}>
                  <span className="text-white text-xl">✓</span>
                </div>
                <p className="font-serif text-xl mb-2">Merci !</p>
                <p className="text-sm leading-relaxed" style={{ color: '#6B6B6B' }}>
                  Ton code est dans ta boîte mail
                </p>
                <p className="text-xs mt-1" style={{ color: '#aaa' }}>
                  (pense à vérifier tes spams)
                </p>
                <button
                  onClick={dismiss}
                  className="mt-6 text-xs underline underline-offset-2 transition-colors"
                  style={{ color: '#D8BAC3' }}
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
